import { NextSeo, NextSeoProps } from 'next-seo'
import { useTheme } from 'next-themes'
import { useRouter } from 'next/compat/router'
import dynamic from 'next/dynamic'
import React, { useEffect, useState } from 'react'
import AuthModal from '~/src/components/AuthModal'
import { isOnboardingPreviewUnlocked } from '~/src/components/dev/isOnboardingPreviewUnlocked'
import ErrorToast from '~/src/components/ErrorToast'
import Footer from '~/src/components/Footer'
import LayoutHeader from '~/src/components/LayoutHeader'
import { Toaster } from '~/src/components/ui/sonner'
import { useAppContext } from '~/src/context/AppProvider'
import { cn } from '~/src/lib/utils'

// Mirrors `theme.extend.colors.darkest` in tailwind.config.ts. Kept as a
// literal because importing the tailwind config here client-bundles the whole
// build toolchain (tailwindcss, @tailwindcss/typography, postcss-selector-parser
// — ~30 KB gz on every page).
const DARKEST_THEME_COLOR = 'hsl(var(--darkest))'

// Onboarding preview launcher. Ships in EVERY env (incl. production) so the
// unlisted `?obkey=<token>` prod preview works (DUP-1861) — it can no longer be
// tree-shaken out. The dynamic import MUST stay a bare `() => import(...)`:
// next/dynamic's compiler only registers a loadable chunk when the import is
// statically analyzable, so wrapping it in a conditional loader silently breaks
// the load. Instead, the chunk cost is avoided by gating the *render* below
// (`<DevOnboardingLauncher/>` only mounts once the preview is unlocked), so
// normal prod traffic never mounts it and never fetches the chunk. Client-only
// (`ssr: false`) because the gate reads `window.location`.
const DevOnboardingLauncher = dynamic(
  () => import('~/src/components/dev/DevOnboardingLauncher'),
  { ssr: false }
)

const EventBanner = dynamic(() => import('~/src/components/EventBanner'), {
  ssr: false,
})

const ExtensionPromoBanner = dynamic(
  () => import('~/src/components/ExtensionPromoBanner'),
  {
    ssr: false,
  }
)

const LayoutAppsHellobar = dynamic(
  () => import('~/src/components/LayoutAppsHellobar'),
  {
    ssr: false,
  }
)

const SmartBanner = dynamic(() => import('~/src/components/SmartBanner'), {
  ssr: false,
})

interface Props extends React.HTMLProps<HTMLDivElement> {
  children?: React.ReactNode
  nextSeo?: NextSeoProps
  hideHeader?: boolean
  hideFooter?: boolean
  bottomReached?: boolean
  hideBorders?: boolean
  LogoComponent?: React.ReactNode
  showSmartBanner?: boolean
  showExtensionHellobar?: boolean
  showEventBanner?: boolean
  fullWidth?: boolean
  minHeight?: boolean
  centerContent?: boolean
  showPoints?: boolean
  stickyHeader?: boolean
  clearBgHeader?: boolean
  backgroundTheme?: string
  showPromoBanner?: boolean
  mainClassName?: string
  headerClassName?: string
  inputClassName?: string
  darkMode?: boolean
  themeOverride?: 'light' | 'dark'
  shadowFooter?: boolean
  /** Hides the header's New Search / auth / avatar cluster (see /partners). */
  hideHeaderActions?: boolean
  /**
   * Renders the desktop primary nav in the header. Set on the pages that nav
   * links to, so the bar persists as you move between them — see `DesktopNav`.
   */
  showDesktopNav?: boolean
}

const Layout: React.FC<Props> = ({
  id,
  children,
  className,
  nextSeo,
  hideHeader,
  hideFooter,
  bottomReached,
  hideBorders,
  LogoComponent,
  showSmartBanner,
  showExtensionHellobar,
  showEventBanner,
  fullWidth,
  minHeight,
  centerContent,
  showPoints,
  stickyHeader,
  clearBgHeader,
  backgroundTheme,
  showPromoBanner,
  mainClassName,
  headerClassName,
  inputClassName,
  darkMode,
  themeOverride,
  shadowFooter,
  hideHeaderActions,
  showDesktopNav,
  ...props
}) => {
  const { errorMessage, setErrorMessage } = useAppContext()
  // next/compat/router: the pages NextRouter under pages/, null under app/.
  const compatRouter = useRouter()
  const { resolvedTheme } = useTheme()
  const isDarkMode = resolvedTheme === 'dark' || darkMode

  // State to hold theme color
  const [themeColor, setThemeColor] = useState<string | null>('#ffffff')

  // Add useEffect to update theme color only on client side
  useEffect(() => {
    const color =
      backgroundTheme || (isDarkMode ? DARKEST_THEME_COLOR : '#ffffff')

    setThemeColor(color)
  }, [backgroundTheme, isDarkMode])

  // This useEffect fix the issue of hydration:
  // A React hydration issue occurs when the initial UI rendered on the client-side
  // does not precisely match the HTML content that was generated and sent from
  // the server during Server-Side Rendering (SSR).
  const [mounted, setMounted] = useState(false)
  useEffect(() => {
    setMounted(true)
  }, [])

  // Only mount the onboarding preview launcher once the env/URL unlocks it, so
  // its (heavy) chunk is never fetched by normal traffic (DUP-1861). Checked
  // post-mount because the gate reads `window.location`.
  const [obPreviewUnlocked, setObPreviewUnlocked] = useState(false)
  useEffect(() => {
    setObPreviewUnlocked(isOnboardingPreviewUnlocked())
  }, [])

  return (
    <>
      {/* next-seo is pages-only: it renders through next/head, which crashes
          during App Router SSR (it resolves the pages-router React → null
          hook dispatcher). Under app/ the Metadata API owns these tags. */}
      {compatRouter !== null && <NextSeo {...nextSeo} />}
      {showSmartBanner && <SmartBanner />}
      {/* Only render meta tag on client after theme is determined */}
      {themeColor && <meta name='theme-color' content={themeColor} />}
      <div
        className={cn({
          dark: isDarkMode && mounted,
        })}
      >
        <main
          id={id}
          data-vaul-drawer-wrapper
          className={cn(
            'flex min-h-[100dvh] flex-col bg-white text-black dark:bg-black dark:text-white/90',
            mainClassName
          )}
        >
          <>
            {showEventBanner && <EventBanner />}
            {!!showExtensionHellobar && <LayoutAppsHellobar />}
            {showPromoBanner && <ExtensionPromoBanner />}
            {!hideHeader && (
              <LayoutHeader
                sticky={stickyHeader}
                expand={!!bottomReached}
                showPoints={showPoints}
                LogoComponent={LogoComponent}
                className={headerClassName}
                inputClassName={inputClassName}
                clearBg={clearBgHeader}
                hideActions={hideHeaderActions}
                showDesktopNav={showDesktopNav}
              />
            )}
          </>

          <div
            className={cn(
              'relative mx-auto w-full flex-grow',
              {
                'min-h-[100dvh]': minHeight,
                'flex flex-col items-center justify-center': centerContent,
                'max-w-screen-sm': !fullWidth,
                'border-black md:border-l-2 md:border-r-2': !hideBorders,
              },
              className
            )}
            {...props}
          >
            {children}

            {errorMessage && (
              <ErrorToast
                errorMessage={errorMessage}
                setErrMsg={setErrorMessage}
              />
            )}
          </div>
          {!hideFooter && <Footer shadow={shadowFooter} />}
          <AuthModal />
          {obPreviewUnlocked && <DevOnboardingLauncher />}
          <Toaster
            position='top-center'
            richColors
            // className='!z-[999]'
          />
        </main>
      </div>
    </>
  )
}

export default React.memo(Layout)
